import { ButtonV3, RightArrowIcon } from '@jouzen/ecom-components';
import { AnimatePresence } from 'framer-motion';
import { cloneElement, type JSX, useRef } from 'react';
import { useButton, useOverlayTrigger } from 'react-aria';
import type { OverlayTriggerProps, OverlayTriggerState } from 'react-stately';
import { useOverlayTriggerState } from 'react-stately';

import type { ProfileData } from '@/app/[locale]/leadership/_data/profiles';
import RichText from '@/app/components/RichText';

import Modal from './Modal';

export interface ModalTriggerProps extends OverlayTriggerProps {
  readonly children: (state: OverlayTriggerState) => JSX.Element;
  readonly profile: ProfileData;
}

const ModalTrigger = ({
  children,
  profile,
  ...props
}: ModalTriggerProps): JSX.Element => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const state = useOverlayTriggerState(props);
  const { overlayProps, triggerProps } = useOverlayTrigger(
    { type: 'dialog' },
    state,
  );
  const { buttonProps } = useButton(triggerProps, buttonRef);

  return (
    <>
      <ButtonV3
        className="relative mt-5 size-11 md:size-auto"
        data-cy="leadership_profile_modal_trigger"
        ref={buttonRef}
        size="small"
        type="button"
        variant="secondary-dark"
        {...buttonProps}
      >
        <span className="max-md:sr-only md:not-sr-only">
          <RichText
            messageKey="leadership_modal_cta"
            tValues={{ firstName: profile.name.split(' ')[0]! }}
          />
        </span>
        <RightArrowIcon className="absolute top-1/2 left-1/2 size-5 -translate-x-1/2 -translate-y-1/2 md:hidden" />
      </ButtonV3>
      <AnimatePresence>
        {state.isOpen && (
          <Modal {...props} state={state}>
            {cloneElement(children(state), overlayProps)}
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default ModalTrigger;
